import React from "react";
import { Typography, Box, useTheme, useMediaQuery, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import ScubaDivingIcon from "@mui/icons-material/ScubaDiving";

// Main glassmorphic header container
const GlassHeaderContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(6),
  padding: theme.spacing(4, 3),
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.85)} 0%, 
    ${alpha(theme.palette.background.paper, 0.65)} 100%)`,
  backdropFilter: "blur(25px) saturate(1.1)",
  WebkitBackdropFilter: "blur(25px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 28,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.08)}`,
  margin: theme.spacing(0, 2),

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.6)}, 
      ${alpha(theme.palette.secondary.main, 0.4)}, 
      transparent)`,
    borderRadius: "28px 28px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 25% 25%, 
      ${alpha(theme.palette.primary.main, 0.04)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 75% 75%, 
      ${alpha(theme.palette.secondary.main, 0.04)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3, 2),
    margin: theme.spacing(0, 1),
  },

  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2.5, 1.5),
    margin: theme.spacing(0, 0.5),
  },
}));

// Enhanced title with gradient text and icon container
const GlassTitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "120%",
    height: "120%",
    background: `radial-gradient(circle, 
      ${alpha(theme.palette.primary.main, 0.05)} 0%, 
      transparent 70%)`,
    borderRadius: "50%",
    zIndex: -1,
    pointerEvents: "none",
  },
}));

const GlassTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main} 0%, 
    ${theme.palette.secondary.main} 70%, 
    ${theme.palette.primary.main} 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
}));

// Enhanced icon with glassmorphic styling
const GlassIcon = styled(ScubaDivingIcon)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.15)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: "50%",
  padding: theme.spacing(1),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.4)}, 
      transparent)`,
    borderRadius: "50% 50% 0 0",
  },

  "&:hover": {
    transform: "translateY(-2px) scale(1.05)",
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.2)} 0%, 
      ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

// Enhanced subtitle with glassmorphic background
const GlassSubtitle = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.primary, 0.85),
  lineHeight: 1.6,
  fontWeight: 400,
  maxWidth: 800,
  margin: "0 auto",
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.4)} 0%, 
    ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: 16,
  padding: theme.spacing(2, 3),
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.2)}, 
      transparent)`,
    borderRadius: "16px 16px 0 0",
  },

  [theme.breakpoints.down("md")]: {
    maxWidth: "90%",
    padding: theme.spacing(1.5, 2.5),
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
    fontSize: "1rem",
    lineHeight: 1.5,
  },
}));

const DashboardHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <GlassHeaderContainer>
      <GlassTitleContainer>
        <GlassIcon
          sx={{
            fontSize: isMobile ? 32 : isTablet ? 36 : 40,
          }}
        />
        <GlassTitle variant={isMobile ? "h4" : "h3"} component="h1">
          Divesites Dashboard
        </GlassTitle>
      </GlassTitleContainer>

      <GlassSubtitle variant={isMobile ? "body1" : "h6"}>
        Monitor multiple dive sites at once for weather, tides, location and
        recent activity. Plan your perfect dive with real-time information.
      </GlassSubtitle>
    </GlassHeaderContainer>
  );
};

export default DashboardHeader;
